import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DatePicker,
  NoResults,
  Notice,
  PageActions,
  Row,
  Select,
  Skeleton,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import TimeClockMirrorTable, { balanceColor, formatSignedHours } from "@components/TimeClockMirrorTable";
import { escapeHtml, usePageHeader } from "@components/ow";
import { DetailTabs } from "../../Common/ContractDetail";
import CompanyDayOffs from "./DayOffs";
import PeriodControls from "./PeriodControls";
import BankEventModal from "./BankEventModal";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";
import { employeeOptionsOf } from "./employeeOptions";
import {
  BANK_EVENT_TYPES,
  formatMinutes,
  formatBalance,
  currentReference,
  fullDate,
  periodFromReference,
  formatPeriodLabel,
  periodFileSuffix,
} from "@helpers/timeClock";
import { appendMirrorSheet, bankEventsMinutes, exportMirror, periodOf, sheetName } from "@helpers/timeClockSheet";

// Ponto CLT da empresa (modelo Detalhe do DS OneWork): para o ADM, abas Espelho de
// ponto e Folgas da empresa, com as ações da página na linha das abas; o gestor de
// ponto (EMP com time_clock:read) vê só o espelho, sem acertos, folgas nem o dia de
// fechamento.

const PAGE_HREF = "/c/time-clock";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
`;

const EmployeeField = styled.div`
  width: 360px;
`;

const CompetenceField = styled.div`
  width: 200px;
`;

/** 'YYYY-MM' ↔ Date do primeiro dia, para o DatePicker de competência. */
const referenceToDate = (reference) => {
  const [year, month] = reference.split("-").map(Number);
  return new Date(year, month - 1, 1);
};
const dateToReference = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export default function TimeClockCompany() {
  const navigate = useNavigate();
  const timeClockService = useService("timeClock");
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth.role === "ADM";

  usePageHeader({ title: "Ponto CLT", breadcrumb: [{ page: "Ponto CLT", route: PAGE_HREF }] });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("espelho");
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState("");
  const [reference, setReference] = useState(currentReference());
  const [pendingCount, setPendingCount] = useState(0);

  // Dia de fechamento da empresa (null = mês calendário) e, opcionalmente, um
  // período avulso que sobrescreve o derivado da competência.
  const [closingDay, setClosingDay] = useState(null);
  const [customPeriod, setCustomPeriod] = useState(null);
  const period = customPeriod || periodFromReference(reference, closingDay);

  const [mirror, setMirror] = useState(null);
  const [mirrorLoading, setMirrorLoading] = useState(false);

  const [bankEvents, setBankEvents] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);

  const [exportingAll, setExportingAll] = useState(false);

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedContractId) {
      fetchMirror();
      if (isAdmin) fetchBankEvents();
    } else {
      setMirror(null);
      setBankEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContractId, period.start, period.end]);

  async function fetchContracts() {
    setLoading(true);
    try {
      const [contractsResult, pendingResult, settingsResult] = await Promise.all([
        timeClockService.listCltContracts(),
        // Só o total: o array sem page cortava em 200
        timeClockService.listAdjustments("status=PENDING&scope=company&page=1&limit=1"),
        timeClockService.getSettings(),
      ]);

      const list = contractsResult?.data?.list || contractsResult?.data || [];
      setContracts(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0) {
        setSelectedContractId(String(list[0].id));
      }
      if (pendingResult && !pendingResult.error) {
        setPendingCount(pendingResult.data?.totalRegisters || 0);
      }
      if (settingsResult && !settingsResult.error) {
        setClosingDay(settingsResult.data?.closing_day ?? null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar contratos CLT");
    }
    setLoading(false);
  }

  async function handleSaveClosingDay(value) {
    const result = await timeClockService.updateSettings({ closing_day: value });
    // Em erro, o sendError do service já mostrou o toast
    if (!result || result.error) return false;
    setClosingDay(result.data?.closing_day ?? null);
    // O período personalizado deixaria a nova configuração sem efeito visível
    setCustomPeriod(null);
    toast.success("Dia de fechamento atualizado!");
    return true;
  }

  async function fetchMirror() {
    setMirrorLoading(true);
    const result = await timeClockService.getMirror(selectedContractId, period);
    // Em erro, o sendError do service já mostrou o toast
    setMirror(result && !result.error ? result.data : null);
    setMirrorLoading(false);
  }

  async function fetchBankEvents() {
    const result = await timeClockService.listBankEvents(`contract_id=${selectedContractId}`);
    if (result && !result.error) {
      setBankEvents(result.data || []);
    }
  }

  function handleExportIndividual() {
    if (!mirror) return;
    try {
      exportMirror(mirror, period);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao exportar Excel");
    }
  }

  async function handleExportAll() {
    setExportingAll(true);
    try {
      const result = await timeClockService.getCompanyMirror(period);
      // Em erro, o sendError do service já mostrou o toast
      if (!result || result.error) {
        setExportingAll(false);
        return;
      }

      const collaborators = result.data?.collaborators || [];
      if (collaborators.length === 0) {
        toast.info("Nenhum colaborador CLT ativo encontrado");
        setExportingAll(false);
        return;
      }

      const { start, end } = periodOf(result.data, period);
      const wb = utils.book_new();

      const consolidated = collaborators.map((item) => ({
        Colaborador: item.user?.name || "-",
        "E-mail": item.user?.email || "-",
        Trabalhado: formatMinutes(item.totals.worked_minutes),
        Esperado: formatMinutes(item.totals.expected_minutes),
        "Saldo do período": formatBalance(item.totals.balance_minutes),
        "Banco anterior": formatBalance(item.bank.previous_balance_minutes),
        "Acertos no período": formatBalance(bankEventsMinutes(item)),
        "Banco final": formatBalance(item.bank.final_balance_minutes),
      }));
      const wsConsolidated = utils.aoa_to_sheet([
        ["Espelho de ponto — consolidado"],
        [`Período: ${formatPeriodLabel(start, end)}`],
        [],
      ]);
      utils.sheet_add_json(wsConsolidated, consolidated, { origin: -1 });
      wsConsolidated["!cols"] = [{ wch: 30 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      utils.book_append_sheet(wb, wsConsolidated, "Consolidado");

      const usedNames = new Set(["Consolidado"]);
      for (const item of collaborators) {
        const name = sheetName(item.user?.name);
        let candidate = name;
        let counter = 2;
        while (usedNames.has(candidate)) {
          candidate = `${name.slice(0, 28)}_${counter}`;
          counter++;
        }
        usedNames.add(candidate);
        appendMirrorSheet(wb, item, candidate, period);
      }

      writeFileXLSX(wb, `espelho_ponto_todos_${periodFileSuffix(start, end)}.xlsx`);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Erro ao exportar Excel");
    }
    setExportingAll(false);
  }

  if (loading) {
    return (
      <ContentPage aria-busy="true">
        <ContentBox>
          <Stack>
            <Skeleton $height={48} />
            <Skeleton $height={56} />
            <Skeleton $height={420} />
          </Stack>
        </ContentBox>
      </ContentPage>
    );
  }

  const selectedContract = contracts.find((contract) => String(contract.id) === selectedContractId);
  const employeeOptions = employeeOptionsOf(contracts);

  const actions = (
    <>
      <Button
        $text={pendingCount > 0 ? `Ajustes de ponto (${pendingCount})` : "Ajustes de ponto"}
        $variant="outline"
        $noWidth
        $action={() => navigate(`${PAGE_HREF}/adjustments`)}
      />
      {isAdmin ? (
        // Gestores de ponto são definidos na tela de permissões
        <Button $text="Gestores de ponto" $variant="outline" $noWidth $action={() => navigate("/c/permissions?grupo=time_clock")} />
      ) : null}
      <Button
        $text="Exportar todos"
        $variant="outline"
        $noWidth
        $isLoading={exportingAll}
        $isDisabled={contracts.length === 0 || exportingAll}
        $action={handleExportAll}
      />
    </>
  );

  const mirrorTab =
    contracts.length === 0 ? (
      <NoResults
        notResultText="Nenhum colaborador com contrato CLT ativo. Crie um contrato com remuneração CLT para habilitar a marcação de ponto."
        includeText={isAdmin ? "Novo contrato" : undefined}
        includeAction={isAdmin ? () => navigate("/c/contracts/new") : undefined}
      />
    ) : (
      <Stack>
        <Row $justifyContent="space-between" $alignItems="flex-end" $gap={16}>
          <Filters>
            <EmployeeField>
              <Select
                $idForm="time-clock-employee"
                $label="Colaborador"
                $options={employeeOptions}
                $textNoOptions="Nenhum colaborador encontrado"
                formatOptionLabel={labelWithHint}
                filterOption={searchLabelAndHint}
                value={employeeOptions.find((option) => option.value === selectedContractId) || null}
                onChange={(option) => option && setSelectedContractId(option.value)}
              />
            </EmployeeField>
            <CompetenceField>
              <DatePicker
                $idForm="time-clock-competence"
                $label="Competência"
                $variant="default"
                $isCompetence
                $date={referenceToDate(reference)}
                $setDate={(date) => date && setReference(dateToReference(date))}
              />
            </CompetenceField>
          </Filters>
          <div>
            <Button
              $text="Exportar espelho"
              $variant="outline"
              $size="small"
              $noWidth
              $isDisabled={!mirror || mirrorLoading}
              $action={handleExportIndividual}
            />
          </div>
        </Row>

        <PeriodControls
          reference={reference}
          period={period}
          closingDay={closingDay}
          isCustom={Boolean(customPeriod)}
          isAdmin={isAdmin}
          onSaveClosingDay={handleSaveClosingDay}
          onApplyCustomPeriod={setCustomPeriod}
          onResetPeriod={() => setCustomPeriod(null)}
        />

        {mirrorLoading ? (
          <Stack>
            <Skeleton $height={56} />
            <Skeleton $height={420} />
          </Stack>
        ) : mirror ? (
          <TimeClockMirrorTable mirror={mirror} showPeriod={false} />
        ) : (
          <NoResults notResultText="Nenhum dado para o período selecionado" />
        )}

        {isAdmin ? (
          <BankEvents events={bankEvents} onAdd={() => setShowBankModal(true)} />
        ) : (
          <Notice
            $variant="gray"
            $text="Acertos de banco de horas (pagamento ou desconto de saldo) são feitos só por administradores."
          />
        )}
      </Stack>
    );

  const tabs = [
    { value: "espelho", label: "Espelho de ponto", content: mirrorTab },
    { value: "folgas", label: "Folgas da empresa", content: <CompanyDayOffs /> },
  ];

  return (
    <ContentPage>
      <ContentBox>
        {isAdmin ? (
          <DetailTabs tabs={tabs} value={activeTab} onChange={setActiveTab} actions={actions} />
        ) : (
          <Stack>
            <PageActions>{actions}</PageActions>
            {mirrorTab}
          </Stack>
        )}
      </ContentBox>

      {showBankModal && selectedContract ? (
        <BankEventModal
          contractId={selectedContract.id}
          employeeName={selectedContract.contractor?.name}
          onClose={() => setShowBankModal(false)}
          onSaved={() => {
            setShowBankModal(false);
            fetchBankEvents();
            fetchMirror();
          }}
        />
      ) : null}
    </ContentPage>
  );
}

const BANK_EVENT_COLUMNS = [
  { id: "date", header: "Data", size: 120, cell: ({ row }) => fullDate(String(row.original.event_date || "").slice(0, 10)) },
  {
    id: "type",
    header: "Tipo",
    size: 180,
    cell: ({ row }) => BANK_EVENT_TYPES[row.original.event_type] || row.original.event_type,
  },
  {
    id: "hours",
    header: "Horas",
    size: 110,
    align: "right",
    cell: ({ row }) => {
      const minutes = Math.round(parseFloat(row.original.hours_delta) * 60);
      return <Text $text={formatSignedHours(minutes)} $variant="paragraph_small_bold" $color={balanceColor(minutes)} />;
    },
  },
  {
    id: "justification",
    header: "Justificativa",
    size: 360,
    cell: ({ row }) => <Text $text={escapeHtml(row.original.justification || "-")} $variant="paragraph_small_medium" $color="N900" />,
  },
  {
    id: "createdBy",
    header: "Lançado por",
    size: 180,
    cell: ({ row }) => <Text $text={escapeHtml(row.original.createdBy?.name || "-")} $variant="paragraph_small_medium" $color="N500" />,
  },
];

/** Acertos do banco de horas do colaborador escolhido (só ADM). */
function BankEvents({ events, onAdd }) {
  return (
    <Stack $gap="12px">
      <Row $justifyContent="space-between" $alignItems="center" $gap={16}>
        <Stack $gap="2px">
          <Text $text="Acertos de banco de horas" $variant="caption_bold" $color="N700" />
          <Text
            $text="Horas pagas ou descontadas na folha e a zeragem do banco."
            $variant="caption_medium"
            $color="N400"
          />
        </Stack>
        <div>
          <Button $text="Lançar acerto" $variant="outline" $size="small" $noWidth $action={onAdd} />
        </div>
      </Row>
      <BasicTable
        data={events}
        columns={BANK_EVENT_COLUMNS}
        getRowId={(event) => String(event.id)}
        noResults={<NoResults notResultText="Nenhum acerto lançado para este colaborador" />}
      />
    </Stack>
  );
}
